/**
 * What a loaded design puts back into the Publish step.
 *
 * A design that was published once carries the shop's title, description
 * and tags in `name`/`description`/`tags`; reopening it must show them
 * again. A draft, however, is created with the localized "untitled"
 * placeholder as its `name` (designStore.createDesign), and that placeholder
 * must not become the listing title: the field stays blank so the seller
 * still has to choose one, exactly as before the hydration existed.
 *
 * The auto-started AI suggestion (useAiProductDescription) fills blank
 * fields; it must never replace a title the shop already has. Whenever a
 * real title comes back, the auto-start is suppressed and the seller keeps
 * "Neu vorschlagen" as an explicit action.
 */

import i18n from 'i18next';
import { SUPPORTED_LANGUAGES } from '@designdeck/shared';
import type { PublishHydration } from '@/stores/publishStore';

export interface PublishHydrationSource {
  name: string;
  description?: string;
  tags?: string[];
}

/**
 * The "untitled" placeholder in every supported language. A design keeps the
 * name it was created with, so a draft opened from a shop in another
 * language still carries the placeholder of the language it was created in.
 */
export function untitledDesignPlaceholders(): string[] {
  return SUPPORTED_LANGUAGES.map((lng) =>
    i18n.t('creator.untitledDesign', { lng })
  );
}

export function resolvePublishHydration(
  design: PublishHydrationSource,
  placeholders: readonly string[]
): PublishHydration {
  const name = design.name.trim();
  const title = name && !placeholders.includes(name) ? name : '';
  return {
    title,
    description: design.description ?? '',
    tags: design.tags ?? [],
    suppressAutoSuggest: title !== '',
  };
}
